/**
 * @fileoverview Input validation and sanitization service.
 * 
 * This service provides comprehensive input validation and sanitization
 * to prevent XSS, injection attacks, and ensure data integrity.
 */

interface ValidationResult {
  isValid: boolean;
  sanitizedValue?: string;
  errors: string[];
}

class InputValidationService {
  private readonly maxLengths = {
    spinnerName: 32,
    participantName: 20,
    questionContent: 500,
    contributionContent: 1000,
    reportReason: 200,
    email: 254
  };

  private readonly allowedHtmlTags = ['b', 'i', 'em', 'strong', 'u'];
  private readonly dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /data:text\/html/gi,
    /vbscript:/gi,
    /expression\s*\(/gi
  ];

  /**
   * Validate and sanitize text input
   */
  validateText(
    value: string, 
    type: keyof typeof this.maxLengths,
    allowHtml: boolean = false
  ): ValidationResult {
    const errors: string[] = [];
    let sanitizedValue = value.trim();

    // Check length
    if (sanitizedValue.length > this.maxLengths[type]) {
      errors.push(`${type} exceeds maximum length of ${this.maxLengths[type]} characters`);
    }

    if (sanitizedValue.length === 0) {
      errors.push(`${type} cannot be empty`);
    }

    // Check for dangerous patterns
    for (const pattern of this.dangerousPatterns) {
      if (pattern.test(sanitizedValue)) {
        errors.push('Potentially dangerous content detected');
        break;
      }
    }

    // Sanitize HTML if not allowed
    if (!allowHtml) {
      sanitizedValue = this.sanitizeHtml(sanitizedValue);
    } else {
      sanitizedValue = this.sanitizeAllowedHtml(sanitizedValue);
    }

    return {
      isValid: errors.length === 0,
      sanitizedValue: errors.length === 0 ? sanitizedValue : undefined,
      errors
    };
  }

  /**
   * Validate email address
   */
  validateEmail(email: string): ValidationResult {
    const errors: string[] = [];
    const sanitizedEmail = email.trim().toLowerCase();

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitizedEmail)) {
      errors.push('Invalid email format');
    }

    if (sanitizedEmail.length > this.maxLengths.email) {
      errors.push('Email exceeds maximum length');
    }

    // Check for dangerous patterns
    for (const pattern of this.dangerousPatterns) {
      if (pattern.test(sanitizedEmail)) {
        errors.push('Potentially dangerous content detected');
        break;
      }
    }

    return {
      isValid: errors.length === 0,
      sanitizedValue: errors.length === 0 ? sanitizedEmail : undefined,
      errors
    };
  }

  /**
   * Validate game question content
   */
  validateGameQuestion(question: {
    content: string;
    options?: string[];
    answer?: string;
  }): ValidationResult {
    const errors: string[] = [];
    let sanitizedContent = '';

    // Validate main content
    const contentResult = this.validateText(question.content, 'questionContent', false);
    if (!contentResult.isValid) {
      errors.push(...contentResult.errors.map(e => `Content: ${e}`));
    } else {
      sanitizedContent = contentResult.sanitizedValue!;
    }

    // Validate options if present
    if (question.options) {
      question.options.forEach((option, index) => {
        const optionResult = this.validateText(option, 'questionContent', false);
        if (!optionResult.isValid) {
          errors.push(...optionResult.errors.map(e => `Option ${index + 1}: ${e}`));
        }
      });
    }

    // Validate answer if present
    if (question.answer) {
      const answerResult = this.validateText(question.answer, 'questionContent', false);
      if (!answerResult.isValid) {
        errors.push(...answerResult.errors.map(e => `Answer: ${e}`));
      }
    }

    return {
      isValid: errors.length === 0,
      sanitizedValue: errors.length === 0 ? sanitizedContent : undefined,
      errors
    };
  }

  /**
   * Sanitize HTML content
   */
  private sanitizeHtml(content: string): string {
    return content
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  /**
   * Sanitize HTML allowing only safe tags
   */
  private sanitizeAllowedHtml(content: string): string {
    // Remove all HTML tags except allowed ones
    const allowedTagsRegex = new RegExp(`<(?!\/?(?:${this.allowedHtmlTags.join('|')})\b)[^>]+>`, 'gi');
    return content.replace(allowedTagsRegex, '');
  }

  /**
   * Validate file upload
   */
  validateFile(file: File, allowedTypes: string[], maxSize: number): ValidationResult {
    const errors: string[] = [];

    if (!allowedTypes.includes(file.type)) {
      errors.push(`File type ${file.type} is not allowed`);
    }

    if (file.size > maxSize) {
      errors.push(`File size ${file.size} bytes exceeds maximum ${maxSize} bytes`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate numeric input
   */
  validateNumber(value: string, min?: number, max?: number): ValidationResult {
    const errors: string[] = [];
    const num = parseFloat(value);

    if (isNaN(num)) {
      errors.push('Value must be a valid number');
    } else {
      if (min !== undefined && num < min) {
        errors.push(`Value must be at least ${min}`);
      }
      if (max !== undefined && num > max) {
        errors.push(`Value must be at most ${max}`);
      }
    }

    return {
      isValid: errors.length === 0,
      sanitizedValue: errors.length === 0 ? num.toString() : undefined,
      errors
    };
  }
}

export const inputValidationService = new InputValidationService();
export default inputValidationService; 